import { useState } from "react";
import { Check, CreditCard, Minus, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useLicense } from "@/context/LicenseContext";

const Subscription = () => {
  const [billingPeriod, setBillingPeriod] = useState("annual"); // "annual" or "monthly"
  const { licenseStatus } = useLicense();

  const plans = [
    {
      id: "officina",
      name: "Officina",
      description: "Pensato per officine che vogliono ordine e velocità",
      monthlyPrice: 24.9,
      annualMonthlyPrice: 19.9,
      annualTotalPrice: 238.8,
      savings: 60,
      monthlyCheckoutUrl: "https://buy.stripe.com/6oU00k4Vv28Lb487aA5kk0k",
      annualCheckoutUrl: "https://buy.stripe.com/5kQ00kfA9dRt5JO8eE5kk0l",
      recommended: false
    },
    {
      id: "gommista",
      name: "Gommista",
      description: "Per chi vuole capacità illimitata e massima continuità operativa",
      monthlyPrice: 34.9,
      annualMonthlyPrice: 29.9,
      annualTotalPrice: 358.8,
      savings: 60,
      monthlyCheckoutUrl: "https://buy.stripe.com/fZu5kE0FfaFh1tycuU5kk0m",
      annualCheckoutUrl: "https://buy.stripe.com/8x25kEgEdcNpa04cuU5kk0n",
      recommended: true
    }
  ];

  const comparisonFeatures = [
    { name: "Pneumatici illimitati", officina: false, gommista: true },
    { name: "Fino a 1200 pneumatici caricati", officina: true, gommista: false },
    { name: "Dispositivi abilitati Smartphone / PC / Tablet illimitati", officina: true, gommista: true },
    { name: "Stampa etichetta codice univoco", officina: true, gommista: true },
    { name: "Stampa QRcode", officina: true, gommista: true },
    { name: "Foto pneumatici", officina: true, gommista: true },
    { name: "Scansione etichetta EPREL", officina: true, gommista: true },
    { name: "Archiviazione personalizzata", officina: true, gommista: true },
    { name: "Statistiche vendite", officina: true, gommista: true },
    { name: "Scarica EXCEL magazzino", officina: true, gommista: true },
    { name: "Inventario magazzino", officina: true, gommista: true },
    { name: "Giacenze / vendita pneumatici", officina: true, gommista: true },
    { name: "Integrazione gestionali esistenti (su richiesta)", officina: true, gommista: true },
    { name: "Importazione del proprio magazzino (su richiesta)", officina: true, gommista: true }
  ];

  const formatPrice = (price) => price.toLocaleString("it-IT", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  });

  const formatSavings = (savings) => savings.toLocaleString("it-IT", {
    minimumFractionDigits: Number.isInteger(savings) ? 0 : 1,
    maximumFractionDigits: 1
  });

  const formatLicenseExpiration = (expiration) => {
    const date = new Date(expiration);

    if (Number.isNaN(date.getTime())) {
      return "Data non disponibile";
    }

    return new Intl.DateTimeFormat("it-IT", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(date);
  };

  const hasLicense = Boolean(licenseStatus?.expireLicenses);

  const handleSubscribe = (plan) => {
    let checkoutUrl = billingPeriod === "monthly"
      ? plan.monthlyCheckoutUrl
      : plan.annualCheckoutUrl;

    try {
      const cachedSession = localStorage.getItem("tyretrack_auth_session");
      if (cachedSession) {
        const sessionData = JSON.parse(cachedSession);
        const userEmail = sessionData?.user?.email;

        if (userEmail) {
          const separator = checkoutUrl.includes("?") ? "&" : "?";
          checkoutUrl = `${checkoutUrl}${separator}prefilled_email=${encodeURIComponent(userEmail)}`;
        }
      }
    } catch (error) {
      console.error("Errore durante la lettura della cache di sessione:", error);
    }

    window.location.assign(checkoutUrl);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-3">
        <div className="flex items-center justify-center gap-2">
          <CreditCard className="text-primary" size={32} />
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
            Area Abbonamenti
          </h1>
        </div>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
          Scegli il piano perfetto per le tue esigenze e inizia a gestire i tuoi pneumatici in modo professionale
        </p>
      </div>

      <Card className="max-w-2xl mx-auto border-border bg-card">
        <CardHeader className="pb-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-xl">La tua licenza</CardTitle>
            {hasLicense && (
              <Badge variant={licenseStatus.isExpired ? "destructive" : "default"}>
                {licenseStatus.isExpired ? "Scaduta" : "Attiva"}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {hasLicense ? (
            <dl className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-lg border border-border bg-secondary/30 p-4">
                <dt className="text-sm text-muted-foreground">Scadenza licenza</dt>
                <dd className="mt-1 font-semibold text-foreground">
                  {formatLicenseExpiration(licenseStatus.expireLicenses)}
                </dd>
              </div>
              <div className="rounded-lg border border-border bg-secondary/30 p-4">
                <dt className="text-sm text-muted-foreground">Numero massimo pneumatici</dt>
                <dd className="mt-1 font-semibold text-foreground">
                  {licenseStatus.maxTyreNo === null ? "Illimitato" : licenseStatus.maxTyreNo}
                </dd>
              </div>
            </dl>
          ) : (
            <p className="text-center text-muted-foreground">Nessuna licenza attiva</p>
          )}
        </CardContent>
      </Card>

      {/* Billing Toggle */}
      <div className="flex items-center justify-center gap-3 sm:gap-4 p-4 bg-card border border-border rounded-lg max-w-md mx-auto mb-12">
        <Label 
          htmlFor="billing-toggle" 
          className={`font-semibold cursor-pointer ${billingPeriod === "monthly" ? "text-foreground" : "text-muted-foreground"}`}
        >
          Mensile
        </Label>
        <Switch
          id="billing-toggle"
          checked={billingPeriod === "annual"}
          onCheckedChange={(checked) => setBillingPeriod(checked ? "annual" : "monthly")}
          className="data-[state=checked]:bg-primary"
        />
        <Label 
          htmlFor="billing-toggle" 
          className={`font-semibold cursor-pointer ${billingPeriod === "annual" ? "text-foreground" : "text-muted-foreground"}`}
        >
          Annuale
        </Label>
        {billingPeriod === "annual" && (
          <span className="px-3 py-1 bg-primary text-primary-foreground rounded-full text-sm font-semibold whitespace-nowrap">
            Risparmia 60€ all'anno
          </span>
        )}
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
        {plans.map((plan) => {
          const isAnnual = billingPeriod === "annual";
          const currentPrice = isAnnual ? plan.annualMonthlyPrice : plan.monthlyPrice;
          const isRecommended = plan.recommended;

          return (
            <div
              key={plan.id}
              className={`bg-card rounded-xl p-8 transition-all flex flex-col justify-between ${
                isRecommended
                  ? "border-2 border-primary shadow-xl md:scale-105 relative"
                  : "border border-border hover:border-primary/50"
              }`}
            >
              {isRecommended && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1 px-4 py-1 bg-primary text-primary-foreground rounded-full text-sm font-semibold shadow-lg">
                    <Sparkles size={14} className="mr-1" />
                    Consigliato
                  </span>
                </div>
              )}

              <div className="text-center pb-6 border-b border-border">
                <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                <p className="text-muted-foreground mb-6">{plan.description}</p>
                <div className="mb-4">
                  {isAnnual && (
                    <span className="text-2xl text-muted-foreground line-through mr-2">
                      {formatPrice(plan.monthlyPrice)}€
                    </span>
                  )}
                  <span className="text-5xl font-bold">{formatPrice(currentPrice)}€</span>
                  <span className="text-muted-foreground">/mese</span>
                </div>
                {isAnnual && (
                  <div>
                    <p className="text-xs tracking-[0.2em] text-muted-foreground mb-2">
                      {formatPrice(plan.annualTotalPrice)}€ /anno
                    </p>
                    <span className="inline-block px-3 py-1 bg-primary/10 border border-primary/20 text-primary rounded-full text-sm font-semibold">
                      Risparmi {formatSavings(plan.savings)}€ all'anno
                    </span>
                  </div>
                )}
              </div>

              <div className="pt-8">
                <Button
                  onClick={() => handleSubscribe(plan)}
                  className={`w-full h-12 font-semibold ${
                    isRecommended
                      ? "bg-primary text-primary-foreground hover:bg-primary/90 btn-glow"
                      : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                  }`}
                >
                  Scegli {plan.name}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Feature Comparison */}
      <div className="overflow-hidden rounded-lg border border-border bg-card max-w-5xl mx-auto">
        <div className="grid grid-cols-[minmax(0,2fr)_minmax(4rem,1fr)_minmax(4rem,1fr)] items-center gap-2 border-b border-border bg-secondary/40 px-4 py-4 sm:gap-4 sm:px-6">
          <span className="text-sm font-semibold text-foreground">Funzionalità</span>
          <span className="text-center text-sm font-semibold text-foreground">Officina</span>
          <span className="rounded-sm bg-primary/15 py-1 text-center text-sm font-semibold text-primary">Gommista</span>
        </div>
        {comparisonFeatures.map((feature, index) => (
          <div
            key={feature.name}
            className={`grid grid-cols-[minmax(0,2fr)_minmax(4rem,1fr)_minmax(4rem,1fr)] items-center gap-2 border-b border-white/10 px-4 py-5 sm:gap-4 sm:px-6 ${
              index % 2 === 0 ? "bg-white/[0.035]" : ""
            }`}
          >
            <span className="text-sm text-muted-foreground sm:text-base">{feature.name}</span>
            <FeatureAvailability included={feature.officina} />
            <div className="-my-5 flex min-h-[3.75rem] items-center justify-center border-x border-primary/30 bg-primary/15 px-2">
              <FeatureAvailability included={feature.gommista} />
            </div>
          </div>
        ))}
      </div>

      {/* Footer Info */}
      <div className="text-center text-sm text-muted-foreground max-w-2xl mx-auto">
        <p>
          Tutti i piani includono aggiornamenti gratuiti e supporto tecnico.
          Puoi annullare o modificare il tuo piano in qualsiasi momento.
        </p>
      </div>
    </div>
  );
};

const FeatureAvailability = ({ included }) => (
  <span className="flex justify-center">
    {included ? (
      <Check size={20} className="text-primary" aria-label="Incluso" />
    ) : (
      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white/70" aria-label="Non incluso">
        <Minus size={16} strokeWidth={2.5} />
      </span>
    )}
  </span>
);

export default Subscription;
